import gql from 'graphql-tag'
import toTitleCase from '../Functions/toTitleCase'

// Every relation option's label starts with its id, so it's unambiguous
// which record you're looking at even when the descriptive part is sparse.
export function decodeGlobalId(globalId) {
    try {
        const parts = atob(globalId).split(':')
        return parts.length === 2 ? parts[1] : globalId
    } catch (e) {
        return globalId
    }
}

function withIdPrefix(pk, descriptor) {
    return descriptor ? `#${pk} - ${descriptor}` : `#${pk}`
}

// resolveById always uses the model's dedicated single-object query (driver,
// car, incident, rep, carIssue, liabilityClaim, subrogationClaim, group), each
// taking the graphene global id -- exact, unambiguous, and cheap. Never the
// list queries: their free-text searchTerm is an OR'd substring match across
// many fields, so a pk like "1" can match dozens of unrelated records.

// --- Driver ---
const DriverOptionsQuery = gql`
    query DriverRelationOptions($searchTerm: String) {
        allDrivers(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk name tlcLicense } }
        }
    }
`
const DriverByIdQuery = gql`
    query DriverById($id: ID) {
        driver(id: $id) { id pk name tlcLicense }
    }
`
function driverOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.tlcLicense ? `${node.name} (${node.tlcLicense})` : node.name) }
}

// --- Car ---
const CarOptionsQuery = gql`
    query CarRelationOptions($searchTerm: String) {
        allCars(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk year vin dmvPlate carModel { make name } } }
        }
    }
`
const CarByIdQuery = gql`
    query CarById($id: ID) {
        car(gqlId: $id) { id pk year vin dmvPlate carModel { make name } }
    }
`
function carOption(node) {
    const modelLabel = [node.year, node.carModel?.make, node.carModel?.name].filter(Boolean).join(' ')
    const idLabel = node.dmvPlate || node.vin || ''
    return { value: node.id, label: withIdPrefix(node.pk, [modelLabel, idLabel].filter(Boolean).join(' - ')) }
}

// --- User ---
const UserOptionsQuery = gql`
    query UserRelationOptions($searchTerm: String) {
        allReps(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk name username email } }
        }
    }
`
const UserByIdQuery = gql`
    query UserById($id: ID!) {
        rep(id: $id) { id pk name username email }
    }
`
function userOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.name || node.username || node.email) }
}

// --- Incident ---
const IncidentOptionsQuery = gql`
    query IncidentRelationOptions($searchTerm: String) {
        allIncidents(searchTerm: $searchTerm, first: 30, orderBy: ["-dateAdded"]) {
            edges { node { id pk title incidentType } }
        }
    }
`
const IncidentByIdQuery = gql`
    query IncidentById($id: ID!) {
        incident(incidentId: $id) { id pk title incidentType }
    }
`
function incidentOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.title || (node.incidentType ? `${node.incidentType} incident` : '')) }
}

// --- CarIssues ("Damage") ---
const CarIssuesOptionsQuery = gql`
    query CarIssuesRelationOptions($searchTerm: String) {
        allCarIssues(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk issueCategory { category } car { vin dmvPlate } } }
        }
    }
`
const CarIssueByIdQuery = gql`
    query CarIssueById($id: ID!) {
        carIssue(id: $id) { id pk issueCategory { category } car { vin dmvPlate } }
    }
`
function carIssueOption(node) {
    const idLabel = node.car?.dmvPlate || node.car?.vin || ''
    return { value: node.id, label: withIdPrefix(node.pk, [node.issueCategory?.category, idLabel].filter(Boolean).join(' - ')) }
}

// --- LiabilityClaim ---
const LiabilityClaimOptionsQuery = gql`
    query LiabilityClaimRelationOptions($searchTerm: String) {
        allLiabilityClaims(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk externalId incident { title } } }
        }
    }
`
const LiabilityClaimByIdQuery = gql`
    query LiabilityClaimById($id: ID!) {
        liabilityClaim(id: $id) { id pk externalId incident { title } }
    }
`
function liabilityClaimOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, [node.externalId, node.incident?.title].filter(Boolean).join(' - ')) }
}

// --- SubrogationClaim ---
const SubrogationClaimOptionsQuery = gql`
    query SubrogationClaimRelationOptions($searchTerm: String) {
        allSubrogationClaims(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk externalId incident { title } } }
        }
    }
`
const SubrogationClaimByIdQuery = gql`
    query SubrogationClaimById($id: ID!) {
        subrogationClaim(id: $id) { id pk externalId incident { title } }
    }
`
function subrogationClaimOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, [node.externalId, node.incident?.title].filter(Boolean).join(' - ')) }
}

// --- Branch ---
// allBranches has no free-text searchTerm; like Group it exposes its model's
// filter_fields, so search by the name icontains filter.
const BranchOptionsQuery = gql`
    query BranchRelationOptions($searchTerm: String) {
        allBranches(name_Icontains: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk name } }
        }
    }
`
const BranchByIdQuery = gql`
    query BranchById($id: ID) {
        branch(id: $id) { id pk name }
    }
`
function branchOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.name) }
}

// --- Line of Business ---
const LineOfBusinessOptionsQuery = gql`
    query LineOfBusinessRelationOptions($searchTerm: String) {
        allLineOfBusiness(name_Icontains: $searchTerm, first: 30) {
            edges { node { id name } }
        }
    }
`
const LineOfBusinessByIdQuery = gql`
    query LineOfBusinessById($id: ID!) {
        lineOfBusiness(id: $id) { id name }
    }
`
function lineOfBusinessOption(node) {
    return { value: node.id, label: withIdPrefix(decodeGlobalId(node.id), node.name) }
}


// --- Group ---
const GroupOptionsQuery = gql`
    query GroupRelationOptions($searchTerm: String) {
        allGroups(name_Icontains: $searchTerm, first: 30) {
            edges { node { id name } }
        }
    }
`
const GroupByIdQuery = gql`
    query GroupById($id: ID!) {
        group(id: $id) { id name }
    }
`
function groupOption(node) {
    return { value: node.id, label: withIdPrefix(decodeGlobalId(node.id), node.name) }
}

// --- Board ---
// Task.board is an FK, so the generic engine advertises `board` with
// relationModel "Board". Without an entry here the popover falls back to a raw
// pk text box -- and boards were the one legacy filter that offered a
// multi-select of titles, so users would have had to know a numeric pk.
// allBoards has no free-text searchTerm; like Group and Branch it exposes its
// model's filter_fields, so search by the title icontains filter.
const BoardOptionsQuery = gql`
    query BoardRelationOptions($searchTerm: String) {
        allBoards(title_Icontains: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk title } }
        }
    }
`
const BoardByIdQuery = gql`
    query BoardById($id: ID!) {
        board(id: $id) { id pk title }
    }
`
function boardOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.title) }
}

// --- TPA ---
const TPAOptionsQuery = gql`
    query TPARelationOptions($searchTerm: String) {
        allTpas(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk name } }
        }
    }
`
const TPAByIdQuery = gql`
    query TPAById($id: ID) {
        tpa(id: $id) { id pk name }
    }
`
function tpaOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.name) }
}

// --- TPAAdjuster ---
const TPAAdjusterOptionsQuery = gql`
    query TPAAdjusterRelationOptions($searchTerm: String) {
        allTpaAdjusters(searchTerm: $searchTerm, first: 30, orderBy: ["-id"]) {
            edges { node { id pk name externalId } }
        }
    }
`
const TPAAdjusterByIdQuery = gql`
    query TPAAdjusterById($id: ID) {
        tpaAdjuster(id: $id) { id pk name externalId }
    }
`
function tpaAdjusterOption(node) {
    const descriptor = node.name || node.externalId || ''
    return { value: node.id, label: withIdPrefix(node.pk, descriptor) }
}

// --- CarModel ---
// allCarModels has no dedicated singular query, but its own `id` filter (an
// exact match against the relay global id, like every Node type here) doubles
// as one: querying the list with `id: <global id>` returns just that record.
const CarModelOptionsQuery = gql`
    query CarModelRelationOptions($searchTerm: String) {
        allCarModels(name_Icontains: $searchTerm, first: 30, orderBy: ["make", "name"]) {
            edges { node { id pk name make series groupType { name } } }
        }
    }
`
const CarModelByIdQuery = gql`
    query CarModelById($id: ID) {
        allCarModels(id: $id) {
            edges { node { id pk name make series groupType { name } } }
        }
    }
`
function carModelOption(node) {
    const descriptor = [toTitleCase(node.make), toTitleCase(node.name)].filter(Boolean).join(' ')
        + (node.series ? ` (${node.series})` : '')
        + (node.groupType?.name ? ` | ${node.groupType.name}` : '')
    return { value: node.id, label: withIdPrefix(node.pk, descriptor) }
}

// --- AgreementType ---
// allAgreementTypes has no dedicated singular query and no exact `id` filter,
// only `id_Icontains` -- since relay global ids are unique per row, an icontains
// match against the full global id string is effectively an exact lookup.
const AgreementTypeOptionsQuery = gql`
    query AgreementTypeRelationOptions($searchTerm: String) {
        allAgreementTypes(searchTerm: $searchTerm, first: 30, orderBy: ["name"]) {
            edges { node { id pk name } }
        }
    }
`
const AgreementTypeByIdQuery = gql`
    query AgreementTypeById($id: ID) {
        allAgreementTypes(id_Icontains: $id) {
            edges { node { id pk name } }
        }
    }
`
function agreementTypeOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.name) }
}

// --- SubscriptionType ---
// allSubscriptionTypes has no dedicated singular query and no exact `id` filter,
// only `id_Icontains`. graphene-django's GlobalIDFilter decodes the global id to
// the raw pk before applying the lookup, so this becomes `id__icontains="3"`,
// which also matches pk 13, 30, 31, etc. -- a best-effort prefix/substring match,
// not an exact lookup.
const SubscriptionTypeOptionsQuery = gql`
    query SubscriptionTypeRelationOptions($searchTerm: String) {
        allSubscriptionTypes(searchTerm: $searchTerm, first: 30, orderBy: ["name"]) {
            edges { node { id pk name } }
        }
    }
`
const SubscriptionTypeByIdQuery = gql`
    query SubscriptionTypeById($id: ID) {
        allSubscriptionTypes(id_Icontains: $id) {
            edges { node { id pk name } }
        }
    }
`
function subscriptionTypeOption(node) {
    return { value: node.id, label: withIdPrefix(node.pk, node.name) }
}

// --- Automation Trigger ---
const AutomationTriggerOptionsQuery = gql`
    query AutomationTriggerRelationOptions($searchTerm: String) {
        allTriggers(searchTerm: $searchTerm, first: 30) {
            edges {
                node {
                    id
                    triggerType
                }
            }
        }
    }
`
const AutomationTriggerByIdQuery = gql`
    query AutomationTriggerById($id: ID!) {
        trigger(id: $id) {
            id
            triggerType
        }
    }
`
function automationTriggerOption(node) {
    return { value: node.id, label: toTitleCase(node.triggerType) }
}

// --- Automation ---
const AutomationOptionsQuery = gql`
    query AutomationRelationOptions($searchTerm: String) {
        allAutomations(searchTerm: $searchTerm, first: 30) {
            edges { node { id name } }
        }
    }
`
const AutomationByIdQuery = gql`
    query AutomationById($id: ID!) {
        automation(id: $id) { id name }
    }
`
function automationOption(node) {
    return { value: node.id, label: toTitleCase(node.name) }
}

// Runs a query and maps a single-node result (`data.<queryFieldPath>`) into an
// option, or null if nothing came back.
function resolveSingular(apolloClient, query, id, fieldPath, mapNode) {
    return apolloClient.query({ query, variables: { id } }).then((result) => {
        const node = result.data?.[fieldPath]
        return node ? mapNode(node) : null
    })
}

// Same idea as resolveSingular, but for models with no dedicated singular query
// -- the by-id query is still a connection, so pull the first (only) edge.
function resolveFirstEdge(apolloClient, query, id, connectionFieldName, mapNode) {
    return apolloClient.query({ query, variables: { id } }).then((result) => {
        const node = result.data?.[connectionFieldName]?.edges?.[0]?.node
        return node ? mapNode(node) : null
    })
}

export const RELATION_OPTIONS_REGISTRY = {
    Driver: {
        query: DriverOptionsQuery,
        getOptions: (data) => (data?.allDrivers?.edges || []).map(({ node }) => driverOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, DriverByIdQuery, id, 'driver', driverOption),
    },
    Car: {
        query: CarOptionsQuery,
        getOptions: (data) => (data?.allCars?.edges || []).map(({ node }) => carOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, CarByIdQuery, id, 'car', carOption),
    },
    User: {
        query: UserOptionsQuery,
        getOptions: (data) => (data?.allReps?.edges || []).map(({ node }) => userOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, UserByIdQuery, id, 'rep', userOption),
    },
    Incident: {
        query: IncidentOptionsQuery,
        getOptions: (data) => (data?.allIncidents?.edges || []).map(({ node }) => incidentOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, IncidentByIdQuery, id, 'incident', incidentOption),
    },
    CarIssues: {
        query: CarIssuesOptionsQuery,
        getOptions: (data) => (data?.allCarIssues?.edges || []).map(({ node }) => carIssueOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, CarIssueByIdQuery, id, 'carIssue', carIssueOption),
    },
    LiabilityClaim: {
        query: LiabilityClaimOptionsQuery,
        getOptions: (data) => (data?.allLiabilityClaims?.edges || []).map(({ node }) => liabilityClaimOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, LiabilityClaimByIdQuery, id, 'liabilityClaim', liabilityClaimOption),
    },
    SubrogationClaim: {
        query: SubrogationClaimOptionsQuery,
        getOptions: (data) => (data?.allSubrogationClaims?.edges || []).map(({ node }) => subrogationClaimOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, SubrogationClaimByIdQuery, id, 'subrogationClaim', subrogationClaimOption),
    },
    Group: {
        query: GroupOptionsQuery,
        getOptions: (data) => (data?.allGroups?.edges || []).map(({ node }) => groupOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, GroupByIdQuery, id, 'group', groupOption),
    },
    Branch: {
        query: BranchOptionsQuery,
        getOptions: (data) => (data?.allBranches?.edges || []).map(({ node }) => branchOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, BranchByIdQuery, id, 'branch', branchOption),
    },
    Board: {
        query: BoardOptionsQuery,
        getOptions: (data) => (data?.allBoards?.edges || []).map(({ node }) => boardOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, BoardByIdQuery, id, 'board', boardOption),
    },
    LineOfBusiness: {
        query: LineOfBusinessOptionsQuery,
        getOptions: (data) => (data?.allLineOfBusiness?.edges || []).map(({ node }) => lineOfBusinessOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, LineOfBusinessByIdQuery, id, 'lineOfBusiness', lineOfBusinessOption),
    },
    TPA: {
        query: TPAOptionsQuery,
        getOptions: (data) => (data?.allTpas?.edges || []).map(({ node }) => tpaOption(node)),
        resolveById: (apolloClient, id) => resolveSingular(apolloClient, TPAByIdQuery, id, 'tpa', tpaOption),
    },
    TPAAdjuster: {
        query: TPAAdjusterOptionsQuery,
        getOptions: (data) => (data?.allTpaAdjusters?.edges || []).map(({ node }) => tpaAdjusterOption(node)),
        resolveById: (apolloClient, id) =>
            resolveSingular(apolloClient, TPAAdjusterByIdQuery, id, 'tpaAdjuster', tpaAdjusterOption),
    },
    CarModel: {
        query: CarModelOptionsQuery,
        getOptions: (data) => (data?.allCarModels?.edges || []).map(({ node }) => carModelOption(node)),
        resolveById: (apolloClient, id) =>
            resolveFirstEdge(apolloClient, CarModelByIdQuery, id, 'allCarModels', carModelOption),
    },
    AgreementType: {
        query: AgreementTypeOptionsQuery,
        getOptions: (data) => (data?.allAgreementTypes?.edges || []).map(({ node }) => agreementTypeOption(node)),
        resolveById: (apolloClient, id) =>
            resolveFirstEdge(apolloClient, AgreementTypeByIdQuery, id, 'allAgreementTypes', agreementTypeOption),
    },
    SubscriptionType: {
        query: SubscriptionTypeOptionsQuery,
        getOptions: (data) => (data?.allSubscriptionTypes?.edges || []).map(({ node }) => subscriptionTypeOption(node)),
        resolveById: (apolloClient, id) =>
            resolveFirstEdge(apolloClient, SubscriptionTypeByIdQuery, id, 'allSubscriptionTypes', subscriptionTypeOption),
    },
    Trigger: {
        query: AutomationTriggerOptionsQuery,
        getOptions: (data) => (data?.allTriggers?.edges || []).map(({ node }) => automationTriggerOption(node)),
        resolveById: (apolloClient, id) =>
            resolveSingular(apolloClient, AutomationTriggerByIdQuery, id, 'trigger', automationTriggerOption),
    },
    Automation: {
        query: AutomationOptionsQuery,
        getOptions: (data) => (data?.allAutomations?.edges || []).map(({ node }) => automationOption(node)),
        resolveById: (apolloClient, id) =>
            resolveSingular(apolloClient, AutomationByIdQuery, id, 'automation', automationOption),
    },
}
